const COUNTRY_PURCHASE_MARKER_KEY = "__countryPurchasePremium";

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeCode = (value) => String(value || "").trim().toLowerCase();

const uniqueCodes = (list) => {
  const acc = [];
  for (const item of list) {
    const code = normalizeCode(item);
    if (!code || acc.includes(code)) continue;
    acc.push(code);
  }
  return acc;
};

const extractPurchases = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (payload?.purchase) return [payload.purchase];
  if (payload && typeof payload === "object") return [payload];
  return [];
};

const getPurchaseDate = (purchase) => {
  const raw = purchase?.updated_at || purchase?.updatedAt || purchase?.created_at || purchase?.createdAt;
  const parsed = new Date(raw || 0).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const getLatestCountryPurchase = (payload) => {
  const purchases = extractPurchases(payload);
  if (purchases.length === 0) return null;
  return [...purchases].sort((a, b) => getPurchaseDate(b) - getPurchaseDate(a))[0];
};

// Reponse de GET /payment/countries/{userId}/purchased : tableau de codes ou d'objets, ou objet enveloppe.
export const getPurchasedCountryCodesFromResponse = (payload) => {
  const raw = Array.isArray(payload)
    ? payload
    : payload?.countries || payload?.codes || payload?.country_codes || payload?.data || payload?.items || [];
  const list = toArray(raw).map((entry) =>
    entry && typeof entry === "object"
      ? entry.code || entry.country_code || entry.countryCode || entry.country
      : entry
  );
  return uniqueCodes(list);
};
export const getCountryPurchaseStatus = (purchase) => {
  return String(purchase?.status || "").toLowerCase();
};

export const getCountryPurchaseId = (purchase) => {
  return purchase?.id ?? purchase?.purchase_id ?? purchase?.purchaseId ?? null;
};

export const getCountryPurchaseCountryCount = (purchase) => {
  const value = purchase?.countryCount ?? purchase?.country_count ?? purchase?.count ?? 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getCountryPurchaseSelectedCodes = (purchase) => {
  const rawCodes =
    purchase?.countryCodes ||
    purchase?.country_codes ||
    purchase?.selected_country_codes ||
    purchase?.selectedCountries ||
    purchase?.countries ||
    [];

  return uniqueCodes(toArray(rawCodes));
};

export const getAccessibleCountryCodesFromPurchase = (purchase, userCountryCode) => {
  const apiAccessible =
    purchase?.accessible_country_codes ||
    purchase?.accessibleCountries ||
    purchase?.countries_accessible ||
    [];

  const selected = getCountryPurchaseSelectedCodes(purchase);
  return uniqueCodes([...(toArray(apiAccessible)), ...selected, userCountryCode]);
};

export const isCountryPurchasePremium = (purchase) => {
  const status = getCountryPurchaseStatus(purchase);
  return status === "paid" || status === "completed";
};

export const isCountryPurchaseMarkerSubscription = (subscriptionObject) => {
  return Boolean(subscriptionObject && subscriptionObject[COUNTRY_PURCHASE_MARKER_KEY] === true);
};

export const clearCountryPurchaseAccess = ({ keepSubscription = true } = {}) => {
  const currentSubscription = safeParse(localStorage.getItem("subscriptions"));
  const markerSubscription = isCountryPurchaseMarkerSubscription(currentSubscription);

  localStorage.removeItem("accessible_country_codes");
  localStorage.removeItem("premium_access_type");
  localStorage.removeItem("country_purchase_state");

  if (!keepSubscription && markerSubscription) {
    localStorage.removeItem("subscriptions");
  }
};

export const applyCountryPurchaseAccess = (user, purchase) => {
  if (!user?.id || !purchase) return false;

  const accessibleCodes = getAccessibleCountryCodesFromPurchase(purchase, user.country);
  const status = getCountryPurchaseStatus(purchase);
  const isPremium = isCountryPurchasePremium(purchase);

  localStorage.setItem("accessible_country_codes", JSON.stringify(accessibleCodes));
  localStorage.setItem("country_purchase_state", JSON.stringify(purchase));

  if (!isPremium) {
    const existing = safeParse(localStorage.getItem("subscriptions"));
    if (isCountryPurchaseMarkerSubscription(existing)) {
      localStorage.removeItem("subscriptions");
    }
    localStorage.removeItem("premium_access_type");
    return false;
  }

  const existingSubscription = safeParse(localStorage.getItem("subscriptions"));
  const hasRealSubscription = existingSubscription && !isCountryPurchaseMarkerSubscription(existingSubscription);

  if (!hasRealSubscription) {
    localStorage.setItem(
      "subscriptions",
      JSON.stringify({
        [COUNTRY_PURCHASE_MARKER_KEY]: true,
        source: "country_purchase",
        status,
        user_id: user.id,
      })
    );
  }

  localStorage.setItem("premium_access_type", hasRealSubscription ? "subscription" : "country_purchase");
  return true;
};

export const getStoredAccessibleCountryCodes = (userCountryCode) => {
  const stored = safeParse(localStorage.getItem("accessible_country_codes"));
  const list = Array.isArray(stored) ? stored : [];
  return uniqueCodes([...list, userCountryCode]);
};
